import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Motorista } from '../../types';
import { motoristasService } from '../../services/motoristas';
import CurrencyInput from '../../components/CurrencyInput';
import { CpfInput, PhoneInput, CepInput } from '../../components/MaskedInputs';
import BackButton from '../../components/BackButton';
import RequisitosDeSenha from '../../components/RequisitosDeSenha';
import { senhaAtendeAPolitica } from '../../utils/politicaDeSenha';
import CampoCidadeUf from '../../components/CampoCidadeUf';
import { buscarCep } from '../../services/localidades';
import { cpfValido } from '../../utils/documentos';

// Formato de e-mail, conferido antes de enviar. Nao substitui a validacao do
// servidor: evita a viagem ate a API por um endereco visivelmente torto.
const EMAIL_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Rotulo de cada campo para o aviso poder dizer "Senha, CPF" em vez de repetir
// o nome tecnico que so existe na API.
const ROTULOS: Record<string, string> = {
  nome: 'Nome', cpf: 'CPF', rg: 'RG', data_nascimento: 'Data de nascimento',
  estado_civil: 'Estado civil', sexo: 'Sexo', telefone: 'Telefone', celular: 'Celular',
  email: 'E-mail', senha: 'Senha', senha_confirmation: 'Confirmar senha', cep: 'CEP',
  endereco: 'Endereço', numero: 'Número', complemento: 'Complemento', bairro: 'Bairro',
  cidade: 'Cidade', estado: 'Estado', cnh: 'Número da CNH', categoria_cnh: 'Categoria da CNH',
  vencimento_cnh: 'Vencimento da CNH', primeira_habilitacao: 'Primeira habilitação',
  data_admissao: 'Data de admissão', data_demissao: 'Data de demissão', status: 'Status',
  salario: 'Salário', observacoes: 'Observações',
};

// Ordem em que os campos aparecem na tela, para levar a pessoa ao primeiro
// problema e nao a um campo qualquer do objeto de erros.
const ORDEM_DOS_CAMPOS = Object.keys(ROTULOS);

const focarPrimeiroErro = (campos: string[]) => {
  const alvo = ORDEM_DOS_CAMPOS.find((campo) => campos.includes(campo)) ?? campos[0];
  const elemento = document.getElementsByName(alvo)[0] as HTMLElement | undefined;
  elemento?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  elemento?.focus({ preventScroll: true });
};

const MotoristasForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);
  const [temAcesso, setTemAcesso] = useState(false);

  // A senha nao faz parte do cadastro do motorista: ela existe para criar o
  // acesso ao portal. Por isso fica em estado proprio e so vai junto no POST.
  const [acesso, setAcesso] = useState({ senha: '', senha_confirmation: '' });

  // O que o servidor recusou, campo a campo. Antes a tela mostrava so o
  // "Dados invalidos" do toast: quem cadastrava reenviava o formulario sem
  // descobrir qual campo estava errado.
  const [erros, setErros] = useState<Record<string, string[]>>({});
  const [cepAviso, setCepAviso] = useState('');
  const [cepBuscando, setCepBuscando] = useState(false);

  const classeCampo = (campo: string) =>
    `w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${
      erros[campo]
        ? 'border-red-500 bg-red-50 focus:ring-red-500'
        : 'border-gray-300 focus:ring-blue-500'
    }`;

  const renderErro = (campo: string) =>
    erros[campo] ? <p className="mt-1 text-xs text-red-600">{erros[campo][0]}</p> : null;

  const alterarAcesso = (campo: 'senha' | 'senha_confirmation', valor: string) => {
    setAcesso((atual) => ({ ...atual, [campo]: valor }));
    setErros((prev) => {
      if (!prev[campo]) return prev;
      const { [campo]: _corrigido, ...resto } = prev;
      return resto;
    });
  };

  const preencherPeloCep = async (digitos: string) => {
    setFormData((prev) => ({ ...prev, cep: digitos }));
    setErros((prev) => {
      if (!prev.cep) return prev;
      const { cep: _corrigido, ...resto } = prev;
      return resto;
    });

    if (digitos.length !== 8) {
      setCepAviso('');
      return;
    }

    setCepBuscando(true);
    setCepAviso('');
    try {
      const endereco = await buscarCep(digitos);
      if (!endereco) {
        setCepAviso('CEP não encontrado. Preencha o endereço manualmente.');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        cep: digitos,
        estado: endereco.uf,
        cidade: endereco.cidade,
        // O que a pessoa ja escreveu nao e sobrescrito: numero e complemento nao
        // vem do CEP e se perderiam junto.
        endereco: (prev.endereco ?? '').trim() === '' ? endereco.logradouro : prev.endereco,
        bairro: (prev.bairro ?? '').trim() === '' ? endereco.bairro : prev.bairro,
      }));
    } catch {
      setCepAviso('Não foi possível consultar o CEP agora. Preencha o endereço manualmente.');
    } finally {
      setCepBuscando(false);
    }
  };

  const apontarErros = (novosErros: Record<string, string[]>) => {
    setErros(novosErros);
    const campos = Object.keys(novosErros);
    toast.error(`Corrija: ${campos.map((campo) => ROTULOS[campo] ?? campo).join(', ')}.`);
    focarPrimeiroErro(campos);
  };

  const [formData, setFormData] = useState<Partial<Motorista>>({
    nome: '',
    cpf: '',
    rg: '',
    data_nascimento: '',
    estado_civil: 'solteiro',
    sexo: 'M',
    telefone: '',
    celular: '',
    email: '',
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cnh: '',
    categoria_cnh: 'B',
    vencimento_cnh: '',
    primeira_habilitacao: '',
    data_admissao: '',
    data_demissao: '',
    status: 'ativo',
    salario: null,
    observacoes: '',
    foto_url: '',
  });

  // Buscar dados do motorista se estiver editando
  const { data: motoristaData, isLoading: isLoadingMotorista } = useQuery({
    queryKey: ['motorista', id],
    queryFn: () => motoristasService.getMotorista(Number(id)),
    enabled: isEditing,
    staleTime: 0, // Sempre buscar dados frescos
  });

  // Mutation para criar/atualizar motorista
  const mutation = useMutation({
    mutationFn: (data: Partial<Motorista>) => {
      if (isEditing) {
        return motoristasService.updateMotorista(Number(id), data);
      }
      return motoristasService.createMotorista(data);
    },
    onSuccess: (response) => {
      if (response.success) {
        toast.success(response.message || (isEditing ? 'Motorista atualizado com sucesso!' : 'Motorista criado com sucesso!'));
        // Invalidar so a lista deixava o detalhe e o proprio formulario
        // servindo o cache anterior a edicao: o registro aparecia atualizado na
        // listagem e desatualizado ao abrir. Sao chaves diferentes, e as duas
        // precisam cair.
        queryClient.invalidateQueries({ queryKey: ['motoristas'] });
        if (id) {
          queryClient.invalidateQueries({ queryKey: ['motorista', id] });
        }
        navigate('/motoristas');
      } else {
        toast.error(response.message || 'Erro ao salvar motorista');
      }
    },
    onError: (error: any) => {
      const errosDoServidor: Record<string, string[]> = error.response?.data?.errors ?? {};
      if (Object.keys(errosDoServidor).length > 0) {
        apontarErros(errosDoServidor);
        return;
      }
      toast.error(error.response?.data?.message || 'Erro ao salvar motorista');
    },
  });

  // Carregar dados do motorista quando disponível
  useEffect(() => {
    if (motoristaData?.data) {
      const motorista = motoristaData.data;
      setTemAcesso(Boolean(motorista.user_id));
      setFormData({
        nome: motorista.nome ?? '',
        cpf: motorista.cpf ?? '',
        rg: motorista.rg ?? '',
        data_nascimento: motorista.data_nascimento ? new Date(motorista.data_nascimento).toISOString().split('T')[0] : '',
        estado_civil: motorista.estado_civil ?? 'solteiro',
        sexo: motorista.sexo ?? 'M',
        telefone: motorista.telefone ?? '',
        celular: motorista.celular ?? '',
        email: motorista.email ?? '',
        cep: motorista.cep ?? '',
        endereco: motorista.endereco ?? '',
        numero: motorista.numero ?? '',
        complemento: motorista.complemento ?? '',
        bairro: motorista.bairro ?? '',
        cidade: motorista.cidade ?? '',
        estado: motorista.estado ?? '',
        cnh: motorista.cnh ?? '',
        categoria_cnh: motorista.categoria_cnh ?? 'B',
        vencimento_cnh: motorista.vencimento_cnh ? new Date(motorista.vencimento_cnh).toISOString().split('T')[0] : '',
        primeira_habilitacao: motorista.primeira_habilitacao ? new Date(motorista.primeira_habilitacao).toISOString().split('T')[0] : '',
        data_admissao: motorista.data_admissao ? new Date(motorista.data_admissao).toISOString().split('T')[0] : '',
        data_demissao: motorista.data_demissao ? new Date(motorista.data_demissao).toISOString().split('T')[0] : '',
        status: motorista.status ?? 'ativo',
        salario: motorista.salario,
        observacoes: motorista.observacoes ?? '',
        foto_url: motorista.foto_url ?? '',
      });

    }
  }, [motoristaData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // O erro some assim que o campo e mexido: manter a borda vermelha em algo
    // que a pessoa acabou de corrigir so confunde.
    setErros(prev => {
      if (!prev[name]) return prev;
      const { [name]: _corrigido, ...resto } = prev;
      return resto;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    

    
    // Filtrar campos vazios e converter tipos
    const submitData: Partial<Motorista> = {
      ...formData,
      estado_civil: formData.estado_civil as 'solteiro' | 'casado' | 'divorciado' | 'viuvo' | 'separado',
      sexo: formData.sexo as 'M' | 'F',
      categoria_cnh: formData.categoria_cnh as 'A' | 'B' | 'C' | 'D' | 'E' | 'AB' | 'AC' | 'AD' | 'AE',
      status: formData.status as 'ativo' | 'inativo' | 'suspenso' | 'licenciado',
      salario: formData.salario ? Number(formData.salario) : null,
      telefone: formData.telefone || null,
      email: formData.email ? formData.email.trim().toLowerCase() : null,
      complemento: formData.complemento || null,
      data_demissao: formData.data_demissao || null,
      observacoes: formData.observacoes || null,
      foto_url: formData.foto_url || null,
    };

    if (!cpfValido(formData.cpf || '')) {
      apontarErros({ cpf: ['CPF inválido. Confira os números.'] });
      return;
    }

    if (submitData.email && !EMAIL_VALIDO.test(submitData.email)) {
      apontarErros({ email: ['E-mail inválido. Confira o endereço, no formato nome@dominio.com.br.'] });
      return;
    }

    if (acesso.senha) {
      // A politica de senha e do servidor; conferir aqui antes evita a ida ate
      // a API so para voltar recusado.
      const errosLocais: Record<string, string[]> = {};
      if (!formData.email) {
        errosLocais.email = ['Informe o e-mail para criar o acesso do motorista.'];
      }
      if (!senhaAtendeAPolitica(acesso.senha)) {
        errosLocais.senha = ['A senha não atende aos requisitos listados abaixo do campo.'];
      }
      if (acesso.senha !== acesso.senha_confirmation) {
        errosLocais.senha_confirmation = ['As senhas não conferem.'];
      }
      if (Object.keys(errosLocais).length > 0) {
        apontarErros(errosLocais);
        return;
      }
      Object.assign(submitData, acesso);
    }

    setErros({});
    mutation.mutate(submitData);
  };







  if (isLoadingMotorista) {
    return (
      <div className="w-full">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <BackButton to="/motoristas" label="Voltar para lista" className="mb-4" />
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Editar motorista' : 'Novo motorista'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEditing ? 'Atualize as informações do motorista' : 'Preencha as informações do novo motorista'}
          </p>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border p-6">
          {/* Dados Pessoais */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Dados pessoais</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('nome')}
                />
                {renderErro('nome')}
              </div>

              <div>
                <CpfInput name="cpf" value={formData.cpf || ''} onChange={handleInputChange} label="CPF" required className={classeCampo('cpf')} />
                {renderErro('cpf')}
                {!erros.cpf && (formData.cpf || '').length === 11 && !cpfValido(formData.cpf || '') && (
                  <p className="mt-1 text-xs text-red-600">CPF inválido. Confira os números.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">RG <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="rg"
                  value={formData.rg}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('rg')}
                />
                {renderErro('rg')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Nascimento <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  name="data_nascimento"
                  value={formData.data_nascimento}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('data_nascimento')}
                />
                {renderErro('data_nascimento')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado Civil <span className="text-red-500">*</span></label>
                <select
                  name="estado_civil"
                  value={formData.estado_civil}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('estado_civil')}
                >
                  <option value="solteiro">Solteiro</option>
                  <option value="casado">Casado</option>
                  <option value="divorciado">Divorciado</option>
                  <option value="viuvo">Viúvo</option>
                  <option value="separado">Separado</option>
                </select>
                {renderErro('estado_civil')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sexo <span className="text-red-500">*</span></label>
                <select
                  name="sexo"
                  value={formData.sexo}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('sexo')}
                >
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                </select>
                {renderErro('sexo')}
              </div>
            </div>
          </div>

          {/* Contato */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contato</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <PhoneInput name="telefone" value={formData.telefone || ''} onChange={handleInputChange} label="Telefone" className={classeCampo('telefone')} />
                {renderErro('telefone')}
              </div>

              <div>
                <PhoneInput name="celular" value={formData.celular || ''} onChange={handleInputChange} label="Celular" required className={classeCampo('celular')} />
                {renderErro('celular')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                              E-mail {acesso.senha && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="email"
                  name="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="nome@dominio.com.br"
                  value={formData.email ?? ''}
                  onChange={handleInputChange}
                  className={classeCampo('email')}
                />
                {renderErro('email')}
              </div>
            </div>
          </div>

          {/* Acesso ao portal. No cadastro define a senha inicial; na edicao
              cria o acesso de quem ainda nao tem ou troca a senha de quem
              esqueceu — o motorista costuma pedir isso a quem administra. */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Acesso ao portal</h2>
            <p className="text-sm text-gray-500 mb-4">
              {temAcesso
                ? 'Este motorista já entra no sistema com o e-mail acima. Deixe a senha em branco para mantê-la como está; preenchendo, você troca a senha dele. No próximo acesso ele terá que escolher uma nova.'
                : 'Opcional. Preenchendo a senha, o motorista passa a entrar no sistema com o e-mail acima e lança os próprios abastecimentos e despesas. No primeiro acesso ele terá que trocar a senha.'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                <input
                  type="password"
                  name="senha"
                  autoComplete="new-password"
                  value={acesso.senha}
                  onChange={(e) => alterarAcesso('senha', e.target.value)}
                  className={classeCampo('senha')}
                />
                {renderErro('senha')}
                <p className="mt-2 text-xs font-medium text-gray-700">A senha precisa ter, obrigatoriamente:</p>
                <RequisitosDeSenha senha={acesso.senha} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar senha</label>
                <input
                  type="password"
                  name="senha_confirmation"
                  autoComplete="new-password"
                  value={acesso.senha_confirmation}
                  onChange={(e) => alterarAcesso('senha_confirmation', e.target.value)}
                  className={classeCampo('senha_confirmation')}
                />
                {renderErro('senha_confirmation')}
                {!erros.senha_confirmation && acesso.senha_confirmation && acesso.senha !== acesso.senha_confirmation && (
                  <p className="mt-1 text-xs text-red-600">As senhas não conferem.</p>
                )}
              </div>
            </div>
          </div>

          {/* Endereço */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Endereço</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <CepInput name="cep" value={formData.cep || ''} onChange={(e) => preencherPeloCep(e.target.value)} label="CEP" required className={classeCampo('cep')} />
                {renderErro('cep')}
                {cepBuscando && <p className="mt-1 text-xs text-gray-500">Buscando endereço...</p>}
                {cepAviso && <p className="mt-1 text-xs text-amber-600">{cepAviso}</p>}
              </div>

              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="endereco"
                  value={formData.endereco}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('endereco')}
                />
                {renderErro('endereco')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="numero"
                  value={formData.numero}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('numero')}
                />
                {renderErro('numero')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                <input
                  type="text"
                  name="complemento"
                  value={formData.complemento ?? ''}
                  onChange={handleInputChange}
                  className={classeCampo('complemento')}
                />
                {renderErro('complemento')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bairro <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="bairro"
                  value={formData.bairro}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('bairro')}
                />
                {renderErro('bairro')}
              </div>

              <CampoCidadeUf
                uf={formData.estado || ''}
                cidade={formData.cidade || ''}
                required
                onChange={({ uf, cidade }) => {
                  setFormData((prev) => ({ ...prev, estado: uf, cidade }));
                  setErros((prev) => {
                    if (!prev.estado && !prev.cidade) return prev;
                    const { estado: _uf, cidade: _cidade, ...resto } = prev;
                    return resto;
                  });
                }}
              />
              {(erros.estado || erros.cidade) && (
                <div className="md:col-span-2 lg:col-span-3">
                  {renderErro('estado')}
                  {renderErro('cidade')}
                </div>
              )}

            </div>
          </div>

          {/* Habilitação */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Habilitação</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número da CNH <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="cnh"
                  value={formData.cnh}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('cnh')}
                />
                {renderErro('cnh')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria <span className="text-red-500">*</span></label>
                <select
                  name="categoria_cnh"
                  value={formData.categoria_cnh}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('categoria_cnh')}
                >
                  <option value="A">A - Motocicletas</option>
                  <option value="B">B - Automóveis</option>
                  <option value="C">C - Caminhões</option>
                  <option value="D">D - Ônibus</option>
                  <option value="E">E - Carretas</option>
                  <option value="AB">AB - Motos e Carros</option>
                  <option value="AC">AC - Motos e Caminhões</option>
                  <option value="AD">AD - Motos e Ônibus</option>
                  <option value="AE">AE - Motos e Carretas</option>
                </select>
                {renderErro('categoria_cnh')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vencimento da CNH <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  name="vencimento_cnh"
                  value={formData.vencimento_cnh}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('vencimento_cnh')}
                />
                {renderErro('vencimento_cnh')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primeira Habilitação <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  name="primeira_habilitacao"
                  value={formData.primeira_habilitacao}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('primeira_habilitacao')}
                />
                {renderErro('primeira_habilitacao')}
              </div>
            </div>
          </div>

          {/* Dados Profissionais */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Dados profissionais</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Admissão <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  name="data_admissao"
                  value={formData.data_admissao}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('data_admissao')}
                />
                {renderErro('data_admissao')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data de Demissão</label>
                <input
                  type="date"
                  name="data_demissao"
                  value={formData.data_demissao ?? ''}
                  onChange={handleInputChange}
                  className={classeCampo('data_demissao')}
                />
                {renderErro('data_demissao')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status <span className="text-red-500">*</span></label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                  className={classeCampo('status')}
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                  <option value="suspenso">Suspenso</option>
                  <option value="licenciado">Licenciado</option>
                </select>
                {renderErro('status')}
              </div>

              <div>
                <CurrencyInput
                  name="salario"
                  value={formData.salario?.toString() || ''}
                  onChange={handleInputChange}
                  label="Salário"
                  className={classeCampo('salario')}
                />
                {renderErro('salario')}
              </div>
            </div>
          </div>

          {/* Observações */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Observações</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
              <textarea
                name="observacoes"
                value={formData.observacoes ?? ''}
                onChange={handleInputChange}
                rows={4}
                className={classeCampo('observacoes')}
                placeholder="Informações adicionais sobre o motorista..."
              />
              {renderErro('observacoes')}

            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/motoristas')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {mutation.isPending ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MotoristasForm;
